<template>
  <div>
    <form @submit.prevent="registerAttempt">
    <div class="form-group" :class="{ error: $v.claimForm.name.$error }">
      <h4>{{ $t('report.model.placeholder.name') }}</h4>
      <input
        :placeholder="$t('report.model.placeholder.name')"
        class="form-control"
        :disabled="loading"
        type="text"
        v-model.lazy="claimForm.name"
        @blur="$v.claimForm.name.$touch"
        autocomplete="name"
      />
      <template v-if="$v.claimForm.name.$error">
        <p class="error-message" v-if="!$v.claimForm.name.required">
          {{ $t("validations.required", { field: $t("fields.name") }) }}
        </p>
      </template>
    </div>

    <!-- EMAIL -->
    <div 
      class="form-group" 
      :class="{ error: $v.claimForm.email.$error }"
      v-if="!isLogged"
    >
      <h4>{{ $t('placeholder.email') }}</h4>
      <input
        :placeholder="$t('report.model.placeholder.email')"
        class="form-control"
        :disabled="loading"
        type="email"
        v-model.lazy="claimForm.email"
        @blur="$v.claimForm.email.$touch"
        autocomplete="email"
      />
      <template v-if="$v.claimForm.email.$error">
        <p class="error-message" v-if="!$v.claimForm.email.required">
          {{ $t("validations.required", { field: $t("fields.email") }) }}
        </p>
        <p class="error-message" v-if="!$v.claimForm.email.email">
          {{ $t("validations.email", { field: $t("fields.email") }) }}
        </p>
      </template>
    </div>
    <!-- EMAIL -->

    <!-- CLAIM -->
    <div
      class="form-group"
      :class="{ error: $v.claimForm.claim.$error }"
    >
      <h4>{{ $t('fields.claim') }}</h4>
      <textarea
        :placeholder="$t('report.model.placeholder.claim')"
        class="form-control"
        :disabled="loading"
        type="text"
        v-model.lazy="claimForm.claim"
        @blur="$v.claimForm.claim.$touch"
        autocomplete="claim"
      />
      <template v-if="$v.claimForm.claim.$error">
        <p class="error-message" v-if="!$v.claimForm.claim.required">
          {{ $t("validations.required", { field: $t("fields.claim") }) }}
        </p>
      </template>
    </div>
    <!-- CLAIM -->

    <!-- ATTACHED FILES -->
    <div class="form-group">
      <h4>{{ $t('report.model.placeholder.attached_files') }}</h4>
      <input
        class="form-control"
        :disabled="loading"
        type="file"
        name="files"
        multiple
        autocomplete="attached_files"
        ref="files"
      />
    </div>
    <!-- ATTACHED FILES -->

    <div
      class="captcha-container"
      :sitekey="recaptchaPublicKey"
      v-if="recaptchaPublicKey"
    >
      <vue-recaptcha
        ref="recaptcha"
        :sitekey="recaptchaPublicKey"
        :loadRecaptchaScript="true"
        :theme="'light'"
        @verify="claimForm.recaptcha = $event"
        @expire="claimForm.recaptcha = null"
      />
      <template v-if="$v.claimForm.recaptcha.$error">
        <p class="error-message" v-if="!$v.claimForm.recaptcha.required">
          {{ $t("validations.captcha") }}
        </p>
      </template>
    </div>

    <div class="messages">
      <div v-for="(errors, errorKey) in claimErrors" :key="errorKey">
        <p v-for="error in errors" :key="error">
          {{ error }}
        </p>
      </div>
    </div>

    <div class="buttons">
      <button class="btn-register" @click="cancelHandler">
        {{ $t("go_back") }}
      </button>
      <button class="btn-register" v-if="!loading" type="submit">
        {{ $t("labels.send") }}
      </button>
      <Spinner v-else />
    </div>
    </form>

    <div class="modal" v-if="showModal">
      <FullBackdrop class="fenif-test">
        <div class="container">
          <h3 class="modal-title" style="color:red;">{{$t('report.title')}}</h3>

          <Spinner v-if="loading"/>

          <template v-else>
            <h4>{{ endedRequestMessage }}</h4>
            <div class="footer">
              <button class="cancel active" @click="hideModal()">
                {{ $t("dashboard.back") }}
              </button>
            </div>
          </template>
        </div>
      </FullBackdrop>
    </div>
  </div>
</template>

<script>
import Spinner from "../Spinner";
import FullBackdrop from "./../FullBackdrop";

import { required, email, requiredIf } from "vuelidate/lib/validators";
import resources from "../../resources";
import VueRecaptcha from "vue-recaptcha";

export default {
  components: {
    Spinner,
    VueRecaptcha,
    FullBackdrop
  },
  data() {
    return {
      claimForm: {
        email: "",
        name: "",
        claim: "",
        recaptcha: null,
      },
      loading: false,
      keepOnView: true,
      showModal: false,
      claimErrors: {},
      endedRequestMessage: this.$t("report.response_message.waiting"),
      endedRequestTimer: null,
    };
  },
  methods: {
    registerAttempt() {
      if (this.$v.claimForm.$invalid) {
        return this.$v.claimForm.$touch();
      }

      const modelId = this.$route.params.modelId;

      this.loading = true;
      this.showModal = true;

      resources.claim.reportModel(modelId, this.claimForm, this.$refs.files.files).then(({ data }) => {
        switch (data.status) {
            case 201:
              this.endedRequestMessage = this.$t(
                "report.response_message.success"
              );
              this.keepOnView = false;
              break;

            case 422:
              this.endedRequestMessage = this.$t(
                "report.response_message.bad_request"
              );
              break;

            case 500:
              this.endedRequestMessage = this.$t("report.response_message.error");
              break;

            default:
              this.endedRequestMessage = this.$t("report.response_message.error");
              break;
          }
      }).catch((error) => {
        console.log(error);
        this.endedRequestMessage = this.$t("report.response_message.error");
        this.claimErrors = error.data.errors || {
          general: [this.$t("error")],
        };
        this.$emit("failed", error);
      }).finally(() => {
        this.loading = false;
        this.endedRequestTimer = setTimeout(() => {
          this.hideModal();
        }, 5000);
      });
    },
    hideModal() {
      if (!this.keepOnView) {
        this.$emit("successfull");
        return;
      }

      this.showModal = false;
    },
    cancelHandler() {
      this.$emit("cancel");
    },
  },
  computed: {
    recaptchaPublicKey() {
      return process.env.VUE_APP_RECAPTCHA_PUBLIC_KEY;
    },
    isLogged() {
      const user = this.$auth.user();
      return user && user.id;
    },
  },
  validations() {
    return {
      claimForm: {
        email: {
          email,
          required: requiredIf()
        },
        name: {
          required,
        },
        claim: {
          required,
        },
        recaptcha: {
          required: requiredIf(() => !this.isLogged),
        },
      },
    };
  },
  watch: {
    loading() {
      this.$emit("loadingChange", this.loading);
    },
  },
  beforeDestroy() {
    clearTimeout(this.endedRequestTimer);
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/classes";
@import "../../styles/variables";

.buttons {
  margin-bottom: 30px;
  display: flex;
  justify-content: space-around;
}

.btn-register {
  @extend %fresh-btn;
  display: block;
  width: 40%;
  border-radius: 5px;
  padding: 0.5em;
}

form h4 {
  margin: unset;
}

.modal {
  position: absolute;
  & .picture {
    width: 5rem;
    height: 5rem;
    margin: 0 auto;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    background-color: #ffffff;
    border-radius: 50%;
    border: 3px solid var(--pallete_main_color);
  }

  & .container {
    box-sizing: border-box;
    border-radius: 1rem;
    border: solid 2px #ccc;
    padding: 1rem;

    @media (min-width: 768px) {
      min-width: 350px;
    }

    & .modal-title {
      font-size: 1.5rem;
      text-transform: uppercase;
    }

    & h3 {
      margin: 0.5rem 0;
    }

    & .footer {
      display: flex;
      justify-content: end;

      & button,
      .action {
        color: inherit;
        border-radius: 0.5rem;
        margin-left: 1rem;
        font-size: 1rem;
        font-weight: bold;
        text-transform: uppercase;
        padding: 0.5rem 1rem;

        &:hover {
          color: var(--pallete_over_main_text_color);
        }
      }

      & .action {
        padding: 0;
      }

      & .action > button {
        margin-left: 0;
      }

      & button.cancel {
        @extend %fresh-btn;

        border-radius: 0.5rem;
      }
    }
  }
}

textarea.form-control {
  resize: vertical;
  max-height: 108px;
  min-height: 54px;
}
</style>
